import React, { useState } from "react";
import {
    format,
    addMonths,
    subMonths,
    startOfMonth,
    endOfMonth,
    eachDayOfInterval,
    isSameMonth,
    isWithinInterval,
    parse,
    isSameDay,
    isAfter,
} from "date-fns";
import { tr } from "date-fns/locale";

function ReservationCalendar({
    reservedDates,
    availableDates,
    onDateSelect,
    selectedStartDate: externalSelectedStartDate,
    selectedEndDate: externalSelectedEndDate,
}) {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedStartDate, setSelectedStartDate] = useState(
        externalSelectedStartDate
    );
    const [selectedEndDate, setSelectedEndDate] = useState(
        externalSelectedEndDate
    );
    const [hoverDate, setHoverDate] = useState(null);

    // Rezerve edilmiş tarihleri Date objesine çevirme
    const reservedDateIntervals =
        reservedDates?.map((dateRange) => ({
            start: parse(dateRange.startDate, "dd-MM-yyyy", new Date()),
            end: parse(dateRange.endDate, "dd-MM-yyyy", new Date()),
        })) || [];

    // Müsait tarihleri Date objesine çevirme
    const availableDateIntervals =
        availableDates?.map((dateRange) => ({
            start: new Date(dateRange.availableFrom),
            end: new Date(dateRange.availableTo),
        })) || [];

    // Tarihin rezerve edilip edilmediğini kontrol etme
    const isDateReserved = (date) => {
        return reservedDateIntervals.some((interval) =>
            isWithinInterval(date, { start: interval.start, end: interval.end })
        );
    };

    // Tarihin müsait olup olmadığını kontrol etme
    const isDateAvailable = (date) => {
        return availableDateIntervals.some((interval) =>
            isWithinInterval(date, { start: interval.start, end: interval.end })
        );
    };

    // Tarih seçimi işlemi
    const handleDateClick = (date) => {
        if (isDateReserved(date) || !isDateAvailable(date)) return;

        if (!selectedStartDate || (selectedStartDate && selectedEndDate)) {
            setSelectedStartDate(date);
            setSelectedEndDate(null);
            onDateSelect(date, null); // Parent komponente bildir
        } else {
            if (isAfter(date, selectedStartDate)) {
                const daysInRange = eachDayOfInterval({
                    start: selectedStartDate,
                    end: date,
                });
                const hasReservedDay = daysInRange.some((day) =>
                    isDateReserved(day)
                );
                const hasUnavailableDay = daysInRange.some((day) =>
                    !isDateAvailable(day)
                );

                if (!hasReservedDay && !hasUnavailableDay) {
                    setSelectedEndDate(date);
                    onDateSelect(selectedStartDate, date); // Parent komponente bildir
                }
            } else {
                setSelectedStartDate(date);
                setSelectedEndDate(null);
                onDateSelect(date, null); // Parent komponente bildir
            }
        }
    };

    // Seçili aralıkta olup olmadığını kontrol etme
    const isInSelectedRange = (date) => {
        if (!selectedStartDate) return false;
        if (!selectedEndDate && hoverDate) {
            return isWithinInterval(date, {
                start: selectedStartDate,
                end: hoverDate,
            });
        }
        if (selectedEndDate) {
            return isWithinInterval(date, {
                start: selectedStartDate,
                end: selectedEndDate,
            });
        }
        return isSameDay(date, selectedStartDate);
    };

    // Ay içindeki tüm günleri alma
    const daysInMonth = eachDayOfInterval({
        start: startOfMonth(currentDate),
        end: endOfMonth(currentDate),
    });

    // Önceki aya gitme
    const prevMonth = () => {
        setCurrentDate(subMonths(currentDate, 1));
    };

    // Sonraki aya gitme
    const nextMonth = () => {
        setCurrentDate(addMonths(currentDate, 1));
    };

    // Seçimi sıfırlama fonksiyonu
    const clearSelection = () => {
        setSelectedStartDate(null);
        setSelectedEndDate(null);
        setHoverDate(null);
        onDateSelect(null, null); // Parent komponente bildir
    };

    return (
        <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 h-[400px] flex flex-col shadow-2xl">
            {/* Başlık ve Navigasyon */}
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white/95 drop-shadow-sm">
                    Rezervasyon Takvimi
                </h3>
                <div className="flex items-center gap-4">
                    <button
                        onClick={prevMonth}
                        className="p-2 rounded-full bg-white/10 backdrop-blur-md hover:bg-white/20 transition-all duration-300 border border-white/10 shadow-lg"
                    >
                        <svg
                            className="w-5 h-5 text-white/90"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 19l-7-7 7-7"
                            />
                        </svg>
                    </button>
                    <span className="text-white/95 font-medium min-w-[120px] text-center drop-shadow-sm">
                        {format(currentDate, "MMMM yyyy", { locale: tr })}
                    </span>
                    <button
                        onClick={nextMonth}
                        className="p-2 rounded-full bg-white/10 backdrop-blur-md hover:bg-white/20 transition-all duration-300 border border-white/10 shadow-lg"
                    >
                        <svg
                            className="w-5 h-5 text-white/90"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                            />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Takvim Container */}
            <div className="flex-1 flex flex-col">
                {/* Haftanın günleri */}
                <div className="grid grid-cols-7 gap-1">
                    {["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"].map(
                        (day) => (
                            <div
                                key={day}
                                className="text-center text-white/70 text-sm py-2 font-medium"
                            >
                                {day}
                            </div>
                        )
                    )}
                </div>

                {/* Takvim günleri */}
                <div className="grid grid-cols-7 gap-1 flex-1">
                    {daysInMonth.map((day, idx) => {
                        const isReserved = isDateReserved(day);
                        const isAvailable = isDateAvailable(day);
                        const isCurrentMonth = isSameMonth(day, currentDate);
                        const isSelected = isInSelectedRange(day);
                        const isStart =
                            selectedStartDate &&
                            isSameDay(day, selectedStartDate);
                        const isEnd =
                            selectedEndDate && isSameDay(day, selectedEndDate);

                        return (
                            <div
                                key={idx}
                                className={`
                                    relative flex items-center justify-center rounded-lg transition-all h-8
                                    ${
                                        isCurrentMonth
                                            ? "text-white/95"
                                            : "text-white/40"
                                    }
                                    ${
                                        isReserved
                                            ? "bg-red-500/20 backdrop-blur-sm cursor-not-allowed border border-red-500/30"
                                            : isAvailable
                                            ? "hover:bg-white/20 hover:backdrop-blur-sm hover:border hover:border-white/30 cursor-pointer"
                                            : "bg-gray-500/10 backdrop-blur-sm cursor-not-allowed border border-gray-500/20 text-white/30"
                                    }
                                    ${isSelected ? "bg-blue-500/20 backdrop-blur-sm border border-blue-500/30" : ""}
                                    ${isStart ? "bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg border border-blue-400/50" : ""}
                                    ${isEnd ? "bg-gradient-to-r from-purple-600 to-blue-500 text-white shadow-lg border border-purple-400/50" : ""}
                                `}
                                onClick={() =>
                                    !isReserved && isAvailable && handleDateClick(day)
                                }
                                onMouseEnter={() =>
                                    !selectedEndDate && isAvailable && setHoverDate(day)
                                }
                                onMouseLeave={() => setHoverDate(null)}
                            >
                                <span className="text-sm font-medium">
                                    {format(day, "d")}
                                </span>
                                {isReserved && (
                                    <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-red-400 shadow-sm"></span>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Açıklama ve Sıfırlama Butonu */}
                <div className="mt-4 pt-3 border-t border-white/10">
                    <div className="flex items-center justify-between">
                        {/* Sol taraftaki açıklamalar */}
                        <div className="flex items-center gap-4 text-sm text-white/70 flex-wrap">
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-red-500/20 border border-red-500/30 backdrop-blur-sm"></div>
                                <span className="font-medium">Rezerve</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-gray-500/10 border border-gray-500/20 backdrop-blur-sm"></div>
                                <span className="font-medium">Müsait Değil</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-blue-500/20 border border-blue-500/30 backdrop-blur-sm"></div>
                                <span className="font-medium">Seçili Aralık</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 shadow-sm"></div>
                                <span className="font-medium">Başlangıç/Bitiş</span>
                            </div>
                        </div>

                        {/* Sağ taraftaki sıfırlama butonu */}
                        {(selectedStartDate || selectedEndDate) && (
                            <button
                                onClick={clearSelection}
                                className="text-sm text-white/70 hover:text-white transition-all duration-300 flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20"
                            >
                                <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                                Seçimi Kaldır
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ReservationCalendar;
